export const RESPONSIVE_BREAKPOINT = {
  SMALL_MOBILE: 375,
  MOBILE: 390,
  TABLET: 768,
  TABLET_LANDSCAPE: 1024,
  LAPTOP: 1280,
  DESKTOP: 1920,
  MONITOR_2K: 2560,
  MONITOR_4K: 3840,
};

export type TDeviceType =
  | 'small-mobile'
  | 'mobile'
  | 'tablet'
  | 'tablet-landscape'
  | 'laptop'
  | 'desktop'
  | 'monitor-2k'
  | 'monitor-4k';

export const getDeviceType = (width: number): TDeviceType => {
  if (width <= RESPONSIVE_BREAKPOINT.SMALL_MOBILE) return 'small-mobile';
  if (width <= RESPONSIVE_BREAKPOINT.MOBILE) return 'mobile';
  if (width <= RESPONSIVE_BREAKPOINT.TABLET) return 'tablet';
  if (width <= RESPONSIVE_BREAKPOINT.TABLET_LANDSCAPE) return 'tablet-landscape';
  if (width <= RESPONSIVE_BREAKPOINT.LAPTOP) return 'laptop';
  if (width <= RESPONSIVE_BREAKPOINT.DESKTOP) return 'desktop';
  if (width <= RESPONSIVE_BREAKPOINT.MONITOR_2K) return 'monitor-2k';
  if (width <= RESPONSIVE_BREAKPOINT.MONITOR_4K) return 'monitor-4k';
  return 'monitor-4k';
};

export const isAnyMobile = (width?: number) => {
  if (!width) return false;
  return getDeviceType(width) === 'small-mobile' || getDeviceType(width) === 'mobile';
};

export const isAnyTablet = (width?: number) => {
  if (!width) return false;
  return getDeviceType(width) === 'tablet' || getDeviceType(width) === 'tablet-landscape';
};

export const isLaptop = (width?: number) => {
  if (!width) return false;
  return getDeviceType(width) === 'laptop';
};

export const isDesktop = (width?: number) => {
  if (!width) return false;
  return getDeviceType(width) === 'desktop';
};

export const isMonitor2k = (width?: number) => {
  if (!width) return false;
  return getDeviceType(width) === 'monitor-2k';
};

export const isMonitor4k = (width?: number) => {
  if (!width) return false;
  return getDeviceType(width) === 'monitor-4k';
};

// Platform/OS and form-factor detection based on user agent (with SSR-safety)
export type TPlatformOS = 'android' | 'ios' | 'windows' | 'macos' | 'linux' | 'chromeos' | 'unknown';

export type TPlatformFormFactor = 'mobile' | 'tablet' | 'desktop';

const getUA = (ua?: string) => ua ?? (typeof navigator !== 'undefined' ? navigator.userAgent : '');

export const getPlatformOS = (ua?: string): TPlatformOS => {
  const U = getUA(ua).toLowerCase();
  if (!U) return 'unknown';
  if (/android/.test(U)) return 'android';
  // iPadOS 13+ may report as Mac; attempt best-effort detection via UA
  if (/iphone|ipad|ipod/.test(U) || (/macintosh/.test(U) && /mobile/.test(U))) return 'ios';
  if (/cros/.test(U)) return 'chromeos';
  if (/win(dows)?/.test(U)) return 'windows';
  if (/mac os x|macintosh/.test(U)) return 'macos';
  if (/linux/.test(U)) return 'linux';
  return 'unknown';
};

export const isAndroidOS = (ua?: string) => getPlatformOS(ua) === 'android';
export const isIOS = (ua?: string) => getPlatformOS(ua) === 'ios';
export const isWindows = (ua?: string) => getPlatformOS(ua) === 'windows';
export const isMacOS = (ua?: string) => getPlatformOS(ua) === 'macos';
export const isLinux = (ua?: string) => getPlatformOS(ua) === 'linux';
export const isChromeOS = (ua?: string) => getPlatformOS(ua) === 'chromeos';

// Form-factor: attempts to detect mobile/tablet/desktop. Optionally leverages width for better accuracy.
export const getPlatformFormFactor = (ua?: string, width?: number): TPlatformFormFactor => {
  const U = getUA(ua);
  const u = U.toLowerCase();

  // Explicit mobile indicators
  const isIPhoneOrIPod = /iphone|ipod/.test(u);
  const isIPad = /ipad/.test(u) || (/macintosh/.test(u) && /mobile/.test(u));
  const isAndroid = /android/.test(u);
  const isAndroidMobile = isAndroid && /mobile/.test(u);
  const isAndroidTablet = isAndroid && !/mobile/.test(u);

  if (isIPhoneOrIPod || isAndroidMobile) return 'mobile';

  // Tablet heuristics: iPad, Android without "Mobile", or width suggests tablet
  if (isIPad || isAndroidTablet || (width ? isAnyTablet(width) : false)) return 'tablet';

  return 'desktop';
};

export const isMobilePlatform = (ua?: string, width?: number) => getPlatformFormFactor(ua, width) === 'mobile';

export const isTabletPlatform = (ua?: string, width?: number) => getPlatformFormFactor(ua, width) === 'tablet';

export const isDesktopPlatform = (ua?: string, width?: number) => getPlatformFormFactor(ua, width) === 'desktop';

// Convenience: "android-mobile", "ios-tablet", etc.
export const getPlatformLabel = (ua?: string, width?: number) => {
  const os = getPlatformOS(ua);
  const ff = getPlatformFormFactor(ua, width);
  return `${os}-${ff}`;
};
